import React, { createContext, useContext, ReactNode, useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTour } from 'hooks/useTour';
import { TOUR_STEPS } from './tour-steps';
import { TourStep } from './tour-step';

interface TourContextType {
  advance: () => void;
  dismiss: () => void;
  isActive: boolean;
  hasFinished: boolean;
  currentStep: number;
}

const TourContext = createContext<TourContextType | null>(null);

export const TourProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isActive, hasFinished, currentStep, advance, dismiss, markSeen } = useTour();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const stepConfig = TOUR_STEPS[currentStep];
  const onCorrectPage =
    stepConfig != null && new RegExp(stepConfig.routePattern).test(pathname);

  // Mark the tour as seen on the server the first time it is actually shown.
  // Previously this only happened on full completion or an explicit Skip, so a
  // user who viewed the tour and then refreshed / navigated away / closed the
  // tab kept `has_seen_tour: false` and saw it again on their next session or a
  // different device. Recording it on first display fixes that re-show.
  const markedSeenRef = useRef(false);
  useEffect(() => {
    if (isActive && onCorrectPage && !markedSeenRef.current) {
      markedSeenRef.current = true;
      markSeen();
    }
  }, [isActive, onCorrectPage, markSeen]);

  const handleAdvance = useCallback(() => {
    const nav = stepConfig?.navigateTo;
    advance();
    if (nav) navigate(nav);
  }, [stepConfig, advance, navigate]);

  return (
    <TourContext.Provider value={{ advance: handleAdvance, dismiss, isActive, hasFinished, currentStep }}>
      {children}
      {isActive && onCorrectPage && (
        <TourStep
          step={stepConfig}
          onNext={handleAdvance}
          onSkip={dismiss}
          onOkay={handleAdvance}
          onDone={handleAdvance}
        />
      )}
    </TourContext.Provider>
  );
};

export const useTourContext = (): TourContextType => {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTourContext must be used inside TourProvider');
  return ctx;
};
